import { Alert, Avatar, Button, Grid, Box, Typography } from "@mui/material";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { ChatMessage, ChatSession } from "../types/chat";

interface ChatNotificationProps {
  message: ChatMessage;
  session: ChatSession;
  onOpenChat: (session: ChatSession) => void;
}

export const ChatNotification = ({
  message,
  session,
  onOpenChat,
}: ChatNotificationProps) => {
  // Obter nome do remetente
  const senderName =
    message.sender_name ||
    message.sender?.nome ||
    message.sender?.nomecomp ||
    session.candidate_name ||
    session.session_name ||
    "Usuário";

  // Obter foto do remetente
  const getSenderAvatar = () => {
    // Para sessões externas (WhatsApp)
    if (session.session_type === "external") {
      return session.candidate_avatar || session.session_avatar;
    }
    // Para sessões internas (usuários do sistema)
    return message.sender_avatar || message.sender?.img_usuario;
  };

  const avatarSrc = getSenderAvatar();

  // Truncar mensagem se muito longa
  const truncateMessage = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Determinar cor e ícone baseado no tipo de sessão
  const getSessionStyle = () => {
    if (session.session_type === "external") {
      return {
        color: "#25D366",
        icon: <WhatsAppIcon sx={{ fontSize: "16px" }} />,
        label: "WhatsApp",
      };
    }
    return {
      color: "#7b1fa2",
      icon: <ChatRoundedIcon sx={{ fontSize: "16px" }} />,
      label: "Chat",
    };
  };

  const sessionStyle = getSessionStyle();

  return (
    <Box
      sx={{
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.02)",
        },
        borderRadius: "8px",
        padding: "4px",
      }}
      onClick={() => onOpenChat(session)}
    >
      <Grid container direction="row" spacing={1}>
        {/* Header com avatar e nome */}
        <Grid item xs={12}>
          <Grid container direction="row" display="flex" alignItems="center">
            <Grid item xs={2}>
              <Avatar
                src={avatarSrc}
                sx={{
                  bgcolor: sessionStyle.color,
                  width: 40,
                  height: 40,
                }}
              >
                {!avatarSrc && senderName.charAt(0).toUpperCase()}
              </Avatar>
            </Grid>
            <Grid item xs={10}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" fontWeight="bold">
                  {senderName}
                </Typography>
                {sessionStyle.icon}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {sessionStyle.label}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Conteúdo da mensagem */}
        <Grid item xs={12}>
          <Alert
            icon={false}
            severity="info"
            sx={{
              pt: 1,
              pb: 1,
              backgroundColor: "rgba(123, 31, 162, 0.08)",
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant="body2" color="text.primary">
              {truncateMessage(message.message_content)}
            </Typography>
          </Alert>
        </Grid>

        {/* Botão de ação */}
        <Grid item xs={12}>
          <Grid
            container
            direction="row"
            alignContent="flex-end"
            justifyContent="flex-end"
          >
            <Button
              startIcon={sessionStyle.icon}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: sessionStyle.color,
                "&:hover": {
                  backgroundColor: sessionStyle.color,
                  filter: "brightness(0.9)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onOpenChat(session);
              }}
            >
              Abrir Conversa
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
